import React, { useState, useEffect, useMemo } from "react";
import { Badge, Button, Collapse, Col, Form, InputGroup, Pagination, Row, Spinner, Table, Card } from "react-bootstrap";
import { FaFilter, FaTimes, FaEye } from "react-icons/fa";
import { FaFolderPlus } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { useProjets } from "../../../hooks/PROJET/useGetProjets";
import NewProject from "../../Modals/Create/NewProject";
import { useAuth } from "../../../context/AuthContext";
import { formatDateShort } from "../../../utils/date";

const MOBILE_BREAKPOINT = 1000;

const ALL_STATUSES = ["open", "in_progress", "closed", "approved"];

const STATUT_BADGE = {
  open:        { bg: "primary",   label: "Ouvert" },
  in_progress: { bg: "warning",   label: "En cours",  text: "dark" },
  closed:      { bg: "secondary", label: "Clôturé" },
  approved:    { bg: "success",   label: "Approuvé" },
};

const OP_OPTIONS = [
  { value: "=",  label: "=" },
  { value: ">=", label: "≥" },
  { value: "<=", label: "≤" },
  { value: ">",  label: ">" },
  { value: "<",  label: "<" },
];

const ProjetsTab = ({ clientId, clientName }) => {
  const { hasPermission }                   = useAuth();
  const [showNewProject, setShowNewProject] = useState(false);
  const [showFilters,    setShowFilters]    = useState(false);
  const [isMobile,       setIsMobile]       = useState(window.innerWidth < MOBILE_BREAKPOINT);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Search & pagination ───────────────────────────────────────────────────
  const [search,          setSearch]          = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page,            setPage]            = useState(1);
  const [limit,           setLimit]           = useState(10);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  // ── Status filter ─────────────────────────────────────────────────────────
  const [activeStatuses, setActiveStatuses] = useState(new Set(ALL_STATUSES));

  const toggleStatus = (s) => {
    setActiveStatuses(prev => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
    setPage(1);
  };

  // ── Advanced filters ──────────────────────────────────────────────────────
  const [filterDescription, setFilterDescription] = useState("");
  const [debouncedDesc,     setDebouncedDesc]     = useState("");
  const [filterDateDebut,   setFilterDateDebut]   = useState({ op: ">=", val: "" });
  const [filterDateFin,     setFilterDateFin]     = useState({ op: "<=", val: "" });
  const [filterDateCloture, setFilterDateCloture] = useState({ op: "=",  val: "" });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedDesc(filterDescription), 500);
    return () => clearTimeout(t);
  }, [filterDescription]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (activeStatuses.size < ALL_STATUSES.length) n++;
    if (debouncedDesc)         n++;
    if (filterDateDebut.val)   n++;
    if (filterDateFin.val)     n++;
    if (filterDateCloture.val) n++;
    return n;
  }, [activeStatuses, debouncedDesc, filterDateDebut, filterDateFin, filterDateCloture]);

  const resetFilters = () => {
    setActiveStatuses(new Set(ALL_STATUSES));
    setFilterDescription("");
    setDebouncedDesc("");
    setFilterDateDebut({ op: ">=", val: "" });
    setFilterDateFin({ op: "<=", val: "" });
    setFilterDateCloture({ op: "=",  val: "" });
    setPage(1);
  };

  // ── Sort ──────────────────────────────────────────────────────────────────
  const [sortField, setSortField] = useState("date_debut");
  const [sortOrder, setSortOrder] = useState("DESC");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortField(field);
      setSortOrder("DESC");
    }
    setPage(1);
  };

  // ── Filters object ────────────────────────────────────────────────────────
  const apiFilters = useMemo(() => ({
    clientId,
    statuses:    activeStatuses.size === ALL_STATUSES.length ? [] : [...activeStatuses],
    description: debouncedDesc,
    dateDebut:   filterDateDebut.val  ? filterDateDebut  : null,
    dateFin:     filterDateFin.val    ? filterDateFin    : null,
    dateCloture: filterDateCloture.val ? filterDateCloture : null,
  }), [clientId, activeStatuses, debouncedDesc, filterDateDebut, filterDateFin, filterDateCloture]);

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data, isLoading, isError } = useProjets(
    page, limit, debouncedSearch, apiFilters, sortField, sortOrder
  );
  const projets    = Array.isArray(data?.data?.projects) ? data.data.projects : [];
  const totalItems = data?.data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));

  // ── Render helpers ────────────────────────────────────────────────────────
  const SortTh = ({ label, field }) => (
    <th
      onClick={() => handleSort(field)}
      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
    >
      {label}{" "}
      {sortField === field
        ? (sortOrder === "ASC" ? "▲" : "▼")
        : <span style={{ opacity: 0.25 }}>▲</span>}
    </th>
  );

  return (
    <>
      {/* Header + Toolbar */}
      <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
        <div className="d-flex align-items-center gap-2 flex-wrap" style={{ flex: "1 1 auto" }}>
          <div style={{ maxWidth: "70px", minWidth: "60px" }}>
            <Form.Select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} size="sm">
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Form.Select>
          </div>
          <div style={{ flex: "1 1 200px", maxWidth: "300px" }}>
            <InputGroup size="sm">
              <Form.Control
                placeholder="Recherche par titre..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="before-input"
              />
            </InputGroup>
          </div>
          <Button
            variant={showFilters || activeFilterCount > 0 ? "warning" : "outline-secondary"}
            size="sm"
            className="d-flex align-items-center gap-2 filter-button"
            onClick={() => setShowFilters(prev => !prev)}
          >
            <FaFilter />
            <span>Filtres</span>
            {activeFilterCount > 0 && (
              <Badge bg="light" text="dark" pill className="ms-1">{activeFilterCount}</Badge>
            )}
          </Button>
        </div>
        {hasPermission("CREATE_PROJECT") && (
          <button
            className="btn btn-info btn-icon-split"
            onClick={() => setShowNewProject(true)}
          >
            <span
              className="icon text-white-50"
              style={{ alignItems: "center", justifyContent: "center" }}
            >
              <FaFolderPlus style={{ color: "#fff" }} />
            </span>
            <span className="text">Nouveau projet</span>
          </button>
        )}
      </div>

      {/* Filter panel */}
      <Collapse in={showFilters}>
        <div>
          <div className="border rounded p-3 mb-3 bg-body-secondary">

            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="fw-semibold text-muted small text-uppercase">Filtres avancés</span>
              {activeFilterCount > 0 && (
                <button className="btn btn-danger btn-icon-split btn-sm" onClick={resetFilters}>
                  <span className="icon text-white-50" style={{ alignItems: "center", justifyContent: "center" }}>
                    <FaTimes style={{ color: "#fff" }} />
                  </span>
                  <span className="text">Réinitialiser</span>
                </button>
              )}
            </div>

            {/* Statut */}
            <div className="mb-3">
              <Form.Label className="small fw-semibold mb-1">Statut</Form.Label>
              <div className="d-flex gap-3 flex-wrap">
                {ALL_STATUSES.map(s => {
                  const b = STATUT_BADGE[s];
                  return (
                    <Form.Check
                      key={s}
                      type="checkbox"
                      id={`ptf-status-${clientId}-${s}`}
                      checked={activeStatuses.has(s)}
                      onChange={() => toggleStatus(s)}
                      label={<Badge bg={b.bg} text={b.text}>{b.label}</Badge>}
                    />
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div className="mb-3">
              <Form.Label className="small fw-semibold mb-1">Description</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filtrer par description..."
                value={filterDescription}
                onChange={e => { setFilterDescription(e.target.value); setPage(1); }}
              />
            </div>

            {/* Dates */}
            <Row className="g-2">
              {[
                { label: "Date de début",    state: filterDateDebut,   setState: setFilterDateDebut,   defaultOp: ">=" },
                { label: "Date de fin",      state: filterDateFin,     setState: setFilterDateFin,     defaultOp: "<=" },
                { label: "Date de clôture",  state: filterDateCloture, setState: setFilterDateCloture, defaultOp: "="  },
              ].map(({ label, state, setState, defaultOp }) => (
                <Col md={4} key={label}>
                  <Form.Label className="small fw-semibold mb-1">{label}</Form.Label>
                  <InputGroup size="sm">
                    <Form.Select
                      value={state.op}
                      onChange={e => setState(prev => ({ ...prev, op: e.target.value }))}
                      style={{ maxWidth: "60px" }}
                    >
                      {OP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Form.Select>
                    <Form.Control
                      type="date"
                      value={state.val}
                      onChange={e => { setState(prev => ({ ...prev, val: e.target.value })); setPage(1); }}
                    />
                    {state.val && (
                      <Button variant="outline-secondary" onClick={() => setState({ op: defaultOp, val: "" })}>
                        <FaTimes size={10} />
                      </Button>
                    )}
                  </InputGroup>
                </Col>
              ))}
            </Row>

          </div>
        </div>
      </Collapse>

      {/* Content */}
      {isLoading ? (
        <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
      ) : isError ? (
        <Card className="border-left-info shadow mb-4">
          <Card.Body>
            <p className="text-danger small">Erreur lors du chargement des projets.</p>
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-left-info shadow mb-4">
          <Card.Body>
            {projets.length === 0 ? (
              <p className="text-muted text-center py-4">Aucun projet trouvé.</p>
            ) : (
              <>
                {isMobile ? (
                  // Mobile view: Cards with border-left
                  <Row className="g-3">
                  {projets.map(p => {
                    const badge = STATUT_BADGE[p.status] ?? { bg: "light", label: p.status };
                    
                    return (
                      <Col xs={12} key={p.id}>
                        <Card className="shadow-sm h-100">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <div className="fw-semibold">{p.title}</div>
                                <small className="text-muted">#{p.id}</small>
                              </div>
                              <Badge bg={badge.bg}>{badge.label}</Badge>
                            </div>

                            <div className="text-muted small mb-1">
                              Début : {p.start_date ? formatDateShort(p.start_date) : "—"}
                            </div>
                            <div className="text-muted small mb-3">
                              Fin prévue : {p.end_date ? formatDateShort(p.end_date) : "—"}
                            </div>

                            <div className="d-flex gap-1 flex-wrap">
                              <Button variant="secondary" size="sm" style={{ width: "32px", height: "32px", padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }} title="Voir le projet">
                                <Link to={`/projet/${p.id}`} style={{ color: "inherit", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <FaEye />
                                </Link>
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              ) : (
                // Desktop view: Table
                <Table striped hover responsive className="align-middle">
                  <thead>
                    <tr>
                      <SortTh label="Titre"      field="titre" />
                      <SortTh label="Statut"     field="status" />
                      <SortTh label="Date début" field="date_debut" />
                      <SortTh label="Date fin"   field="date_fin" />
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {projets.map(p => {
                      const badge = STATUT_BADGE[p.status] ?? { bg: "light", label: p.status };
                      return (
                        <tr key={p.id}>
                          <td>{p.title}</td>
                          <td><Badge bg={badge.bg} text={badge.text}>{badge.label}</Badge></td>
                          <td>{p.start_date ? formatDateShort(p.start_date) : "—"}</td>
                          <td>{p.end_date   ? formatDateShort(p.end_date)   : "—"}</td>
                          <td className="text-center">
                            <Button variant="secondary" size="sm" title="Voir le projet">
                              <Link to={`/projet/${p.id}`} style={{ color: "inherit", textDecoration: "none" }}>
                                <FaEye />
                              </Link>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
              {projets.length > 0 && totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <small className="text-muted">{totalItems} projet{totalItems !== 1 ? "s" : ""}</small>
                  <Pagination size="sm" className="mb-0">
                    <Pagination.Prev onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} />
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                      <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>
                    ))}
                    <Pagination.Next onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
                  </Pagination>
                </div>
              )}
              </>
            )}
            </Card.Body>
          </Card>
      )}

      <NewProject
        showNewProject={showNewProject}
        setShowNewProject={setShowNewProject}
        defaultClient={clientId ? { id: clientId, name: clientName } : null}
      />
    </>
  );
};

export default ProjetsTab;
